import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Select, MenuItem, Grid, Alert, Chip, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditIcon from '@mui/icons-material/Edit';

const AWARD_TYPES = [
  { value: 'cash',    label: 'Cash' },
  { value: 'shirts',  label: 'Shirts' },
  { value: 'hats',    label: 'Hats' },
  { value: 'medals',  label: 'Medals' },
  { value: 'trophy',  label: 'Trophy' },
  { value: 'sunglasses', label: 'Sunglasses' },
  { value: 'other',   label: 'Other' },
];

function fmtDate(iso) {
  if (!iso) return '–';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m - 1]} ${+d}, ${y}`;
}

function emptyAward() { return { type: '', value: '', note: '' }; }

export default function TournamentAdminFinancialDetail() {
  const navigate = useNavigate();
  const { tid } = useParams();
  const [tournament, setTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [awards, setAwards] = useState({}); // { divName: { first: {...}, second: {...} } }
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [tid]);

  async function loadAll() {
    setLoading(true);
    try {
      const tSnap = await getDoc(doc(db, 'tournaments', tid));
      if (!tSnap.exists()) { setError('Tournament not found.'); setLoading(false); return; }
      const t = { id: tSnap.id, ...tSnap.data() };
      setTournament(t);
      // Initialize awards from saved data, or empty for each division
      const divs = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
      const aw = { ...(t.awards || {}) };
      for (const d of divs) {
        if (!aw[d]) aw[d] = { first: emptyAward(), second: emptyAward() };
        if (!aw[d].first) aw[d].first = emptyAward();
        if (!aw[d].second) aw[d].second = emptyAward();
      }
      setAwards(aw);
      // Load registrations to compute teams per division and revenue
      const rSnap = await getDocs(query(
        collection(db, 'registrations'),
        where('tournament_id', '==', tid),
        where('status', '==', 'active'),
      ));
      setRegistrations(rSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }

  function updateAward(div, place, field, value) {
    setAwards(prev => ({
      ...prev,
      [div]: {
        ...prev[div],
        [place]: { ...(prev[div]?.[place] || emptyAward()), [field]: value },
      },
    }));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      // Sanitize: convert string values to numbers, drop empty rows
      const clean = {};
      for (const [div, places] of Object.entries(awards)) {
        const cleanPlaces = {};
        for (const place of ['first', 'second']) {
          const a = places?.[place];
          if (!a) continue;
          if (!a.type && !a.value && !a.note) continue;
          cleanPlaces[place] = {
            type: a.type || '',
            value: parseFloat(a.value) || 0,
            note: a.note || '',
          };
        }
        if (Object.keys(cleanPlaces).length > 0) clean[div] = cleanPlaces;
      }
      await updateDoc(doc(db, 'tournaments', tid), { awards: clean });
      setSavedAt(new Date());
      setEditing(false);
    } catch (e) {
      setError('Save failed: ' + (e.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error && !tournament) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin/financial')} sx={{ mt: 2 }}>
          Back to Financials
        </Button>
      </Container>
    );
  }

  // Compute per-division stats
  const divisions = Array.isArray(tournament.divisions) ? tournament.divisions : JSON.parse(tournament.divisions || '[]');
  // Tournament prices map: { divisionName: dollars } — may be stored as a JSON string
  const prices = typeof tournament.prices === 'string'
    ? (() => { try { return JSON.parse(tournament.prices); } catch { return {}; } })()
    : (tournament.prices || {});
  const tournamentPriceDollars = tournament.price_dollars || 0;
  const divStats = divisions.map(div => {
    const divRegs = registrations.filter(r => r.division === div);
    const teams = divRegs.length;
    const rawFee = (prices[div] != null) ? Number(prices[div]) : tournamentPriceDollars;
    const fallbackFeePerTeam = isNaN(rawFee) ? 0 : rawFee;
    // Income per team:
    //   1. Use registration's amount_paid (Stripe truth — handles merged-division pricing)
    //   2. Fall back to tournament.prices[division] (for onsite/cash payments)
    const income = divRegs.reduce((acc, r) => {
      if (r.amount_paid && r.amount_paid > 0) return acc + (r.amount_paid / 100);
      return acc + fallbackFeePerTeam;
    }, 0);
    // Average fee per team (for display in the Fee/Team column)
    const feePerTeam = teams > 0 ? income / teams : fallbackFeePerTeam;
    // Expenses for this division (first + second)
    const a = awards[div] || {};
    const firstCost = parseFloat(a.first?.value) || 0;
    const secondCost = parseFloat(a.second?.value) || 0;
    const expenses = firstCost + secondCost;
    return { name: div, teams, feePerTeam, income, expenses, net: income - expenses };
  });

  const totals = divStats.reduce((acc, d) => ({
    teams:    acc.teams + d.teams,
    income:   acc.income + d.income,
    expenses: acc.expenses + d.expenses,
  }), { teams: 0, income: 0, expenses: 0 });
  totals.net = totals.income - totals.expenses;

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">📊 {tournament.name}</Typography>
              <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />{fmtDate(tournament.date)}
                {tournament.location && <> &nbsp;·&nbsp;<LocationOnIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />{tournament.location}</>}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {editing ? (
                <>
                  <Button variant="contained" color="success" startIcon={<SaveIcon />}
                          onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Awards'}
                  </Button>
                  <Button variant="outlined" color="inherit"
                          onClick={() => { setEditing(false); loadAll(); }}
                          sx={{ borderColor: 'rgba(255,255,255,0.4)' }}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="contained" color="warning" startIcon={<EditIcon />}
                        onClick={() => setEditing(true)}>
                  Edit Awards
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container sx={{ pb: 6 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {savedAt && !editing && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSavedAt(null)}>
            Awards saved.
          </Alert>
        )}

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={2} sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', borderLeft: '4px solid #1a2940' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase' }}>Teams</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a2940' }}>{totals.teams}</Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={2} sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', borderLeft: '4px solid #27AE60' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase' }}>Income</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#27AE60' }}>${totals.income.toFixed(2)}</Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={2} sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', borderLeft: '4px solid #E74C3C' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase' }}>Award Expenses</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#E74C3C' }}>${totals.expenses.toFixed(2)}</Typography>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card elevation={2} sx={{ p: 2.5, borderRadius: 3, textAlign: 'center', borderLeft: `4px solid ${totals.net >= 0 ? '#C9A84C' : '#E74C3C'}` }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase' }}>Net</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: totals.net >= 0 ? '#C9A84C' : '#E74C3C' }}>
                ${totals.net.toFixed(2)}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Divisions table */}
        <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEventsIcon sx={{ color: '#C9A84C' }} />
            <Typography fontWeight="bold">Divisions, Income & Awards</Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f4f6f9' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Division</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Teams</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Fee/Team</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Income</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 240 }}>1st Place Award</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 240 }}>2nd Place Award</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Expenses</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {divStats.map(div => (
                <TableRow key={div.name}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{div.name}</TableCell>
                  <TableCell align="center"><Chip label={div.teams} size="small" /></TableCell>
                  <TableCell align="right">${div.feePerTeam.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ color: '#27AE60', fontWeight: 600 }}>
                    ${div.income.toFixed(2)}
                  </TableCell>
                  {['first', 'second'].map(place => {
                    const a = awards[div.name]?.[place] || emptyAward();
                    return (
                      <TableCell key={place}>
                        {editing ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', minWidth: 220 }}>
                            <Select size="small" value={a.type} displayEmpty
                                    onChange={e => updateAward(div.name, place, 'type', e.target.value)}
                                    sx={{ minWidth: 110, fontSize: '0.85rem' }}>
                              <MenuItem value="">— Type —</MenuItem>
                              {AWARD_TYPES.map(t => (
                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                              ))}
                            </Select>
                            <TextField size="small" type="number" value={a.value}
                                       onChange={e => updateAward(div.name, place, 'value', e.target.value)}
                                       placeholder="$"
                                       inputProps={{ step: '0.01', min: '0' }}
                                       sx={{ width: 90 }} />
                            {a.type === 'other' && (
                              <TextField size="small" value={a.note}
                                         onChange={e => updateAward(div.name, place, 'note', e.target.value)}
                                         placeholder="Describe"
                                         sx={{ flex: 1, minWidth: 100 }} />
                            )}
                          </Box>
                        ) : (
                          (a.type || a.value) ? (
                            <Box>
                              <Chip label={a.type ? AWARD_TYPES.find(t => t.value === a.type)?.label || a.type : 'Set'}
                                    size="small" sx={{ fontSize: '0.7rem' }} />
                              {a.value > 0 && (
                                <Typography variant="caption" sx={{ ml: 1, color: '#E74C3C', fontWeight: 600 }}>
                                  ${parseFloat(a.value).toFixed(2)}
                                </Typography>
                              )}
                              {a.note && (
                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                  · {a.note}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">— Not set —</Typography>
                          )
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ color: '#E74C3C', fontWeight: 600 }}>
                    ${div.expenses.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: div.net >= 0 ? '#C9A84C' : '#E74C3C' }}>
                    ${div.net.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin/financial')} variant="outlined">
            Back to Financials
          </Button>
          <Button onClick={() => navigate(`/tournament-admin/registrations?tid=${tid}`)} variant="outlined" color="primary">
            View Registrations
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
